import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './EmbedForm.scss'
import {contentLabel} from 'lib/styleguide/base.scss'
import {CTA} from 'common/styleguide/CTA'
import {ListMinimal} from 'common/styleguide/ListMinimal'
import cx from 'classnames'
import {POITagRowContainer} from './POITagRow'
import {RouteRow} from './RouteRow'
import * as actions from 'lib/api/embeds/regional'
import {ConfirmationDialog} from 'common/styleguide/ConfirmationDialog'
import {tileServices} from 'lib/tileServices'
import _ from 'lodash'
import {ROUTE_COLORS} from 'features/embed/RegionalEmbed/RegionalEmbedlet'
import {Icon} from 'common/Icon'

const ROUTES_PAGE_SIZE = 5

export class EmbedForm extends React.PureComponent {
  constructor (props) {
    super(props)

    this.throttledSaveName = _.debounce(this.saveName, 750)

    this.state = {
      showRemoveTagConfirm: false,
      showEmbedCodeDialog: false,
      routeTag: props.routeTag || '',
      name: props.name || '',
      routesExpanded: false
    }
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.routeTag !== this.props.routeTag) {
      this.setState({routeTag: '', routesExpanded: false})
    }

    if (prevProps.name !== this.props.name) {
      this.setState({name: this.props.name})
    }
  }

  showEmbedCodeDialog = (e) => {
    e.preventDefault()
    this.setState({showEmbedCodeDialog: true})
  }

  hideEmbedCodeDialog = () => {
    this.setState({showEmbedCodeDialog: false})
  }

  showRemoveTagConfirm = (e) => {
    e.preventDefault()
    this.setState({showRemoveTagConfirm: true})
  }

  hideRemoveTagConfirm = () => {
    this.setState({showRemoveTagConfirm: false})
  }

  removeRouteTag = () => {
    const {removeRouteTag, saving} = this.props

    if (saving) return

    this.hideRemoveTagConfirm()
    removeRouteTag()
  }

  pickRouteTag = (e) => {
    e.preventDefault()
    const {pickRouteTag} = this.props
    const {routeTag} = this.state

    if (routeTag) {
      pickRouteTag(routeTag)
    }
  }

  addCategoryTagClicked = (e) => {
    const {addCategoryPoiTag, saving} = this.props
    const tag = this._categoryPoi.value
    if (!saving && tag) addCategoryPoiTag(tag)
  }

  addRestrictivePoiClicked = () => {
    const {addRestrictivePoiTag, saving} = this.props
    const tag = this._restrictivePoi.value
    if (!saving && tag) addRestrictivePoiTag(tag)
  }

  removeRestrictivePoiClicked = (e) => {
    const {removeRestrictivePoiTag, saving} = this.props
    e.preventDefault()
    if (!saving) removeRestrictivePoiTag()
  }

  metricUnitsChanged = (e) => {
    const {updateMetricUnits} = this.props
    updateMetricUnits(e.target.value === 'metric')
  }

  overlayChanged = (e) => {
    const {updateOverlay} = this.props
    updateOverlay(e.target.value)
  }

  boundsCriteriaChanged = (e) => {
    const {updateBoundsCriteria} = this.props
    updateBoundsCriteria(e.target.value)
  }

  onSubmit = (e) => {
    e.preventDefault()
  }

  routeTagChanged = (e) => {
    const tag = e.target.value
    this.setState({routeTag: tag})
  }

  nameChanged = (e) => {
    this.setState({name: e.target.value}, this.throttledSaveName)
  }

  saveName = () => {
    const {name} = this.state
    const {updateName} = this.props
    updateName(name)
  }

  toggleRoutesExpanded = () => {
    this.setState((prevState) => ({routesExpanded: !prevState.routesExpanded}))
  }

  render () {
    const {
      id,
      routes,
      colors,
      urls,
      poiTags,
      availablePoiTags,
      availableRouteTags,
      metricUnits,
      overlay,
      restrictivePoiTag,
      boundsCriteria
    } = this.props
    const {
      showRemoveTagConfirm,
      showEmbedCodeDialog,
      name,
      routesExpanded
    } = this.state

    const colorsMap = (colors && colors.Route) || {}
    const urlsMap = (urls && urls.Route) || {}

    const availableRestrictiveTags = availablePoiTags && availablePoiTags.map(t => <option value={t} key={t}>{t}</option>)
    const availableCategoryTags = availablePoiTags && poiTags && _.difference(availablePoiTags, poiTags.map(t => t.tag)).map(t => <option value={t} key={t}>{t}</option>)

    const routeTagValue = (!this.state.routeTag ? this.props.routeTag : this.state.routeTag) || ''
    const routeTagIsSet = !!this.props.routeTag

    const routesToShow = routes && (routesExpanded ? routes : routes.take(ROUTES_PAGE_SIZE))

    return (
      <div className={styles.EmbedForm}>
        <form onSubmit={this.onSubmit}>
          <div className={styles.section}>
            <h2>Customize Your Embed</h2>
            <input type='text' placeholder='Enter embed title' name='title' value={name} onChange={this.nameChanged} />
          </div>

          <div className={styles.section}>
            <h3>Add a route</h3>
            <div>
              <div data-tip title='All routes in your route library with this tag will appear on the embed.' className={styles.helpIcon}><Icon i='ios-information-circle-outline' size={20} /></div>
              <label htmlFor='routeTag' className={cx(contentLabel, styles.labelMargin)}>Route tag</label>
            </div>
            {!routeTagIsSet && <div className={styles.horizontal}>
              <div className={styles.item}>
                <select className={styles.input} name='routeTag' value={routeTagValue} onChange={this.routeTagChanged}>
                  <option value=''>Pick a route tag</option>
                  {availableRouteTags && availableRouteTags.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className={styles.item}>
                <CTA button inline margins={false} onClick={this.pickRouteTag}>Add</CTA>
              </div>
            </div>}
            {routeTagIsSet && <ListMinimal className={styles.FullBleedList}>
              <li>
                <div>{routeTagValue}</div>
                <div className={styles.listControls}><a href='#' onClick={this.showRemoveTagConfirm}>Remove</a></div>
              </li>
            </ListMinimal>}
            {routeTagIsSet && routes && <Fragment>
              <label className={cx(contentLabel, styles.labelMargin)}>Routes included</label>
              <ListMinimal className={styles.FullBleedList}>
                {routesToShow.valueSeq().map((r, i) => <RouteRow key={r.id} id={r.id} name={r.name} color={colorsMap[r.id] || ROUTE_COLORS[i % ROUTE_COLORS.length]} customColor={colorsMap[r.id]} url={urlsMap[r.id]} />)}
                {!routesExpanded && routes.size > ROUTES_PAGE_SIZE && <div className={styles.routesToggleRow}><a onClick={this.toggleRoutesExpanded}>Show {routes.size - ROUTES_PAGE_SIZE} more routes</a></div>}
                {routesExpanded && routes.size > ROUTES_PAGE_SIZE && <div className={styles.routesToggleRow}><a onClick={this.toggleRoutesExpanded}>Collapse routes list</a></div>}
              </ListMinimal>
            </Fragment>}
          </div>

          <div className={styles.section}>
            <h3>Add POI</h3>
            <div>
              <div data-tip title='Restrict the POI categories to a specific tag. E.g. input "Portland" to show only Portland tagged POI of any category.' className={styles.helpIcon}><Icon i='ios-information-circle-outline' size={20} /></div>
              <label htmlFor='restrictivePoi' className={cx(contentLabel, styles.labelMargin)}>Restrictive POI</label>
            </div>
            {!restrictivePoiTag && <div className={styles.horizontal}>
              <div className={styles.item}>
                <select name='restrictivePoi' className={styles.input} ref={r => { this._restrictivePoi = r }}>
                  <option value=''>Pick a restrictive tag</option>
                  {availableRestrictiveTags}
                </select>
              </div>
              <div className={styles.item}>
                <CTA button inline margins={false} onClick={this.addRestrictivePoiClicked}>Add</CTA>
              </div>
            </div>}
            {restrictivePoiTag && <ListMinimal className={styles.FullBleedList}>
              <li>
                <div>{restrictivePoiTag}</div>
                <div className={styles.listControls}><a href='#' onClick={this.removeRestrictivePoiClicked}>Remove</a></div>
              </li>
            </ListMinimal>}
            <div>
              <div data-tip title='POI in your POI library with these tags and the selected restrictive tags will appear on the embed' className={styles.helpIcon}><Icon i='ios-information-circle-outline' size={20} /></div>
              <label htmlFor='categoryPoi' className={cx(contentLabel, styles.labelMargin)}>Category POI</label>
            </div>
            <div className={styles.horizontal}>
              <div className={styles.item}>
                <select name='categoryPoi' className={styles.input} ref={r => { this._categoryPoi = r }}>
                  <option value=''>Pick a category tag</option>
                  {availableCategoryTags}
                </select>
              </div>
              <div className={styles.item}>
                <CTA button inline margins={false} onClick={this.addCategoryTagClicked}>Add</CTA>
              </div>
            </div>
            {poiTags && <ListMinimal className={styles.FullBleedList}>
              {poiTags.map((poiTag) => (<POITagRowContainer key={poiTag.tag} {...poiTag} />))}
            </ListMinimal>}
          </div>

          <div className={styles.section}>
            <div>
              <div data-tip title='Select the map type you want for your embed, and the units in which information will be displayed.' className={styles.helpIcon}><Icon i='ios-information-circle-outline' size={20} /></div>
              <h3>Customize Your Map</h3>
            </div>
            <div className={styles.horizontal}>
              <div className={cx(styles.item, styles.itemHalf)}>
                Map type
                <select name='overlay' onChange={this.overlayChanged} value={overlay}>
                  {_.filter(tileServices, s => s.type !== 'google').map(ts => <option value={ts.id} key={ts.id}>{ts.name}</option>)}
                </select>
              </div>
              <div className={cx(styles.item, styles.itemHalf)}>
                Units
                <select name='metricUnits' onChange={this.metricUnitsChanged} value={metricUnits ? 'metric' : 'imperial'}>
                  <option value='imperial'>Imperial (mi/ft)</option>
                  <option value='metric'>Metric (km/m)</option>
                </select>
              </div>
            </div>
            <div className={styles.horizontal}>
              <div className={styles.item}>
                Zoom to
                <select name='boundsCriteria' className={styles.input} onChange={this.boundsCriteriaChanged} value={boundsCriteria}>
                  <option value='all'>Routes and POI</option>
                  <option value='routes'>Routes only</option>
                </select>
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <CTA secondary onClick={this.showEmbedCodeDialog}>Embed Code</CTA>
          </div>
        </form>

        {showRemoveTagConfirm && <ConfirmationDialog
          confirmText='Remove'
          onRequestClose={this.hideRemoveTagConfirm}
          onCancel={this.hideRemoveTagConfirm}
          onConfirm={this.removeRouteTag}>
          Are you sure you want to remove the Route Tag? This will remove all routes, and any urls and colors you've picked.
        </ConfirmationDialog>}

        {showEmbedCodeDialog && <ConfirmationDialog
          title={'Embed code for '}
          confirmText='Got it!'
          onRequestClose={this.hideEmbedCodeDialog}
          onCancel={this.hideEmbedCodeDialog}
          onConfirm={this.hideEmbedCodeDialog}>
          <textarea className={styles.embedCode} defaultValue={`<iframe src="https://ridewithgps.com/embeds?type=region&id=${id}" style="width: 1px; min-width: 100%; height: 700px; border: none;" scrolling="no"></iframe>`} />
        </ConfirmationDialog>}
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const embed = state.regionalEmbed
  const embedData = embed.get('embedData')

  return {
    id: embedData.get('id'),
    name: embedData.get('name'),
    metricUnits: embedData.getIn(['embedSettings', 'metricUnits']),
    overlay: embedData.getIn(['embedSettings', 'overlay']),
    routeTag: embedData.get('routeTag'),
    routes: embed.get('routes'),
    availablePoiTags: embed.get('availablePoiTags'),
    availableRouteTags: embed.get('availableRouteTags'),
    colors: embedData.get('colors'),
    urls: embedData.get('urls'),
    poiTags: embedData.get('poiTags'),
    boundsCriteria: embedData.get('boundsCriteria'),
    saving: embed.get('saving'),
    restrictivePoiTag: embedData.get('restrictivePoiTag')
  }
}

const mapDispatchToProps = {
  removeRouteTag: actions.removeRouteTag,
  updateMetricUnits: actions.updateMetricUnits,
  updateOverlay: actions.updateOverlay,
  updateBoundsCriteria: actions.updateBoundsCriteria,
  pickRouteTag: actions.pickRouteTag,
  addCategoryPoiTag: actions.addCategoryPoiTag,
  addRestrictivePoiTag: actions.addRestrictivePoiTag,
  removeRestrictivePoiTag: actions.removeRestrictivePoiTag,
  updateName: actions.updateName
}

export const EmbedFormContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(EmbedForm)
